import React, { useState, useEffect, useLayoutEffect, useRef, useContext, Suspense } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  getProject, updateProject, updateProjectStatus, deleteProject,
  getProjectFeatures, getAllFeatures,
  addPersonaToProject, removePersonaFromProject,
  getProjectPersonasCategories,
  getProjectCategories, updateProjectCategoryOverride,
  getProjectTasks, createTask, updateTaskStatus, deleteTask,
  getProjectSummary,
  getSummaryConfigs, summariesForProject, SummaryConfig,
  Project, ProjectStatus, ProjectCategory, Feature, Persona, CategorizedPersonas, ManualOverrideType, Task, TaskStatus, TaskCreate, ProjectSummary
} from '../api/client'
import FeatureSummaryCard from '../components/summaries/FeatureSummaryCard'
import AddSummaryButton from '../components/summaries/AddSummaryButton'
import { motion } from 'motion/react'
import { useNotify } from '../components/notice/useNotify'
import { UserContext } from '../components/UserContext'
import PersonaCard from '../components/PersonaCard'
import PersonaCreator from '../components/PersonaCreator'
import CreateAdvisorCard from '../components/CreateAdvisorCard'
import PersonaSearch from '../components/PersonaSearch'
import Spinner from '../components/Spinner'
import ProjectActivityCalendar from '../components/ProjectActivityCalendar'
import ProjectPlacesSection from '../components/project/ProjectPlacesSection'
import ProjectPeopleSection from '../components/project/ProjectPeopleSection'
import TriggeredAdviceCard from '../components/project/TriggeredAdviceCard'
import AdvisorSuggestionsList from '../components/project/AdvisorSuggestionsList'
import EmptyProjectNudge from '../components/project/EmptyProjectNudge'
import ProjectInsightsCard from '../components/summaries/ProjectInsightsCard'
import { useAutoAnimate } from '@formkit/auto-animate/react'
const ProjectFeatures = React.lazy(() => import('../components/ProjectFeatures'))

const ProjectDetailPage: React.FC = () => {
  const notify = useNotify()
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const [taskListParent] = useAutoAnimate<HTMLDivElement>()

  const [project, setProject] = useState<Project | null>(null)
  const [associatedFeatureIds, setAssociatedFeatureIds] = useState<number[]>([])
  const [allFeatures, setAllFeatures] = useState<Feature[]>([])
  const [summaryConfigs, setSummaryConfigs] = useState<SummaryConfig[]>([])
  const [categorizedPersonas, setCategorizedPersonas] = useState<CategorizedPersonas>({
    project_personas: [],
    bench_personas: [],
    all_personas: []
  })
  const [projectSummary, setProjectSummary] = useState<ProjectSummary | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingStates, setLoadingStates] = useState({
    project: true,
    features: true,
    personas: true,
    tasks: true,
    summary: true
  })
  // Lifetime-empty signals from the three lower sections — null = unknown
  // (default to showing the wrapper until the section confirms emptiness,
  // so we don't pop sections in after first paint).
  const [activityEmpty, setActivityEmpty] = useState<boolean | null>(null)
  const [placesEmpty, setPlacesEmpty] = useState<boolean | null>(null)
  const [peopleEmpty, setPeopleEmpty] = useState<boolean | null>(null)
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [showPersonaCreator, setShowPersonaCreator] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [allCategories, setAllCategories] = useState<ProjectCategory[]>([])
  const [showCategorySelector, setShowCategorySelector] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchResults, setSearchResults] = useState<Persona[]>([])
  const [projectPersonaIds, setProjectPersonaIds] = useState<number[]>([])
  
  
  // Tasks state
  const [tasks, setTasks] = useState<Task[]>([])
  const [taskStatusFilter, setTaskStatusFilter] = useState<TaskStatus | 'all'>('all')
  const [showAddTask, setShowAddTask] = useState(false)
  const [newTaskText, setNewTaskText] = useState('')
  const [newTaskDueDate, setNewTaskDueDate] = useState('')
  const [newTaskDueTime, setNewTaskDueTime] = useState('')
  const [savingTask, setSavingTask] = useState(false)
  
  const [editedProject, setEditedProject] = useState<{
    title: string,
    description: string,
    emoji: string,
    config: Record<string, any>
  }>({
    title: '',
    description: '',
    emoji: '',
    config: {}
  })
  
  // Load project and associated data on component mount
  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusMenuOpen) {
        const target = event.target as Node;
        const dropdown = document.querySelector('.status-dropdown-container');
        if (dropdown && !dropdown.contains(target)) {
          setStatusMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusMenuOpen]);

  const reloadSummaryConfigs = React.useCallback(() => {
    getSummaryConfigs()
      .then(setSummaryConfigs)
      .catch((e) => console.error('Failed to load summaries', e))
  }, [])
  useEffect(() => { reloadSummaryConfigs() }, [reloadSummaryConfigs])

  useEffect(() => {
    if (!projectId) return

    const fetchData = async () => {
      setLoading(true)
      setLoadingStates({
        project: true,
        features: true,
        personas: true,
        tasks: true,
        summary: true
      })

      try {
        // Fetch all data in parallel
        const [
          fetchedProject,
          featureIds,
          features,
          personas,
          categories,
          projectTasks,
          summary
        ] = await Promise.all([
          getProject(parseInt(projectId)),
          getProjectFeatures(parseInt(projectId)),
          getAllFeatures(),
          getProjectPersonasCategories(parseInt(projectId)),
          getProjectCategories(),
          getProjectTasks(parseInt(projectId)),
          getProjectSummary(parseInt(projectId))
        ])

        // Set all state at once
        setProject(fetchedProject)
        setAssociatedFeatureIds(featureIds)
        setAllFeatures(features)
        setCategorizedPersonas(personas)
        setProjectPersonaIds(personas.project_personas.map(p => p.id))
        setAllCategories(categories)
        setTasks(projectTasks)
        setProjectSummary(summary)

        // Initialize edited project with current values
        setEditedProject({
          title: fetchedProject.title,
          description: fetchedProject.description || '',
          emoji: fetchedProject.emoji || '',
          config: fetchedProject.config || {}
        })

        setLoadingStates({
          project: false,
          features: false,
          personas: false,
          tasks: false,
          summary: false
        })
      } catch (err) {
        notify.error('Failed to load project details')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    
    fetchData()
  }, [projectId])
  
  
  const handleStatusChange = async (newStatus: ProjectStatus) => {
    if (!projectId || !project) return
    
    try {
      const updatedProject = await updateProjectStatus(parseInt(projectId), newStatus)
      setProject(updatedProject)
      notify.success('Project status updated successfully')
    } catch (err) {
      notify.error('Failed to update project status')
      console.error(err)
    }
  }
  
  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!projectId || saving) return
    
    setSaving(true)
    try {
      // Format the project data properly before sending to API
      const projectToUpdate = {
        ...editedProject,
        // Convert empty strings to undefined
        description: editedProject.description ? editedProject.description : undefined,
        emoji: editedProject.emoji ? editedProject.emoji : undefined
      }
      
      const updatedProject = await updateProject(parseInt(projectId), projectToUpdate)
      setProject(updatedProject)
      setEditMode(false)
      notify.success('Project updated successfully')
    } catch (err) {
      notify.error('Failed to update project: ' + (err.response?.data?.detail || err.message || 'Unknown error'))
      console.error(err)
    } finally {
      setSaving(false)
    }
  }
  
  const handleFeaturesUpdate = (featureIds: number[], features: Feature[]) => {
    setAssociatedFeatureIds(featureIds)
    setAllFeatures(features)
  }
  
  const handleAddPersona = async (personaId: number) => {
    if (!projectId) return
    
    try {
      await addPersonaToProject(parseInt(projectId), personaId)
      // Refresh persona list
      const personas = await getProjectPersonasCategories(parseInt(projectId))
      setCategorizedPersonas(personas)
      // Update project persona IDs
      setProjectPersonaIds(personas.project_personas.map(p => p.id))
    } catch (err) {
      notify.error('Failed to add team member to project')
      console.error(err)
    }
  }
  
  const handleRemovePersona = async (personaId: number) => {
    if (!projectId) return
    
    try {
      await removePersonaFromProject(parseInt(projectId), personaId)
      // Refresh persona list
      const personas = await getProjectPersonasCategories(parseInt(projectId))
      setCategorizedPersonas(personas)
      // Update project persona IDs
      setProjectPersonaIds(personas.project_personas.map(p => p.id))
      notify.success('Team member removed from project successfully')
    } catch (err) {
      notify.error('Failed to remove team member from project')
      console.error(err)
    }
  }
  
  const handleDeleteProject = async () => {
    if (!projectId) return
    
    try {
      await deleteProject(parseInt(projectId))
      notify.success('Project deleted successfully')
      setTimeout(() => {
        navigate('/projects')
      }, 1500)
    } catch (err) {
      notify.error('Failed to delete project')
      console.error(err)
    }
    setShowDeleteConfirm(false)
  }
  
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  const formatMonthYear = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  const descriptionRef = useRef<HTMLParagraphElement | null>(null)
  const [descExpanded, setDescExpanded] = useState(false)
  const [descOverflows, setDescOverflows] = useState(false)
  useLayoutEffect(() => {
    const el = descriptionRef.current
    if (!el) {
      setDescOverflows(false)
      return
    }
    // Measure against the clamped state — when expanded the element is unclamped
    // and would report scrollHeight == clientHeight even for long text.
    if (descExpanded) return
    setDescOverflows(el.scrollHeight > el.clientHeight + 1)
  }, [project?.description, descExpanded])
  
  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-blush-light">
        <div className="container mx-auto px-4 pb-8 sm:pt-8 flex justify-center items-center flex-grow">
            <Spinner />
        </div>
      </div>
    )
  }
  
  if (!project) {
    return (
      <div className="flex flex-col min-h-screen bg-blush-light">
        <div className="container mx-auto px-4 pb-8 sm:pt-8"/>
      </div>
    )
  }
  
  
  // Helper function to check if a persona is in the project
  const isInProject = (personaId: number) => {
    return projectPersonaIds.includes(personaId) || 
           categorizedPersonas.project_personas.some(p => p.parent_persona_id === personaId)
  }
  
  // Handler for persona updates
  const handlePersonaUpdated = async (updatedPersona: Persona) => {
    try {
      // Update the persona in the project personas
      const updatedProjectPersonas = categorizedPersonas.project_personas.map(persona => 
        persona.id === updatedPersona.id ? updatedPersona : persona
      );
      
      // Update the persona in the bench personas
      const updatedBenchPersonas = categorizedPersonas.bench_personas.map(persona => 
        persona.id === updatedPersona.id ? updatedPersona : persona
      );
      
      // Update the state with the updated personas
      setCategorizedPersonas({
        ...categorizedPersonas,
        project_personas: updatedProjectPersonas,
        bench_personas: updatedBenchPersonas
      });
      
      notify.success(`Updated ${updatedPersona.first_name}'s details successfully`);
    } catch (err) {
      notify.error('Failed to update persona details');
      console.error(err);
    }
  }

  const handleCategoryToggle = async (category: ProjectCategory) => {
    if (!project || !projectId) return

    const isCurrentlyAssigned = project.categories?.some(c => c.id === category.id)
    
    try {
      if (isCurrentlyAssigned) {
        // Check if it has a manual override that's not DELETE
        const existingCategory = project.categories?.find(c => c.id === category.id)
        
        if (existingCategory?.manual_override === ManualOverrideType.DELETE) {
          // If it was previously marked as DELETE, update to KEEP_VALUE since user is checking it
          await updateProjectCategoryOverride(parseInt(projectId), category.id, ManualOverrideType.KEEP_VALUE)
        } else {
          // For non-DELETE categories, mark with DELETE when unchecking
          await updateProjectCategoryOverride(parseInt(projectId), category.id, ManualOverrideType.DELETE)
        }
      } else {
        // Add this category with manual override since user explicitly added it
        await updateProjectCategoryOverride(parseInt(projectId), category.id, ManualOverrideType.KEEP_VALUE)
      }

      // Refresh project to get updated categories
      const updatedProject = await getProject(parseInt(projectId))
      setProject(updatedProject)
      notify.success('Category updated successfully')
    } catch (err) {
      notify.error('Failed to update category')
      console.error(err)
    }
  }

  // Task handlers
  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTaskText.trim() || !projectId || savingTask) return
    
    setSavingTask(true)
    try {
      let due_at = undefined
      let due_date = undefined
      
      if (newTaskDueDate) {
        if (newTaskDueTime) {
          // Create datetime string for due_at
          due_at = `${newTaskDueDate}T${newTaskDueTime}:00`
        } else {
          // Just set due_date for date-only tasks
          due_date = newTaskDueDate
        }
      }
      
      const taskData: TaskCreate = {
        text_body: newTaskText.trim(),
        due_date,
        due_at
      }
      
      await createTask(taskData, parseInt(projectId))
      
      // Refresh tasks
      const updatedTasks = await getProjectTasks(parseInt(projectId))
      setTasks(updatedTasks)
      
      // Reset form
      setNewTaskText('')
      setNewTaskDueDate('')
      setNewTaskDueTime('')
      setShowAddTask(false)
      
      notify.success('Task created successfully')
    } catch (err) {
      notify.error('Failed to create task')
      console.error(err)
    } finally {
      setSavingTask(false)
    }
  }
  
  const handleTaskStatusChange = async (taskId: number, newStatus: TaskStatus) => {
    try {
      await updateTaskStatus(taskId, newStatus)
      
      // Refresh tasks
      if (projectId) {
        const updatedTasks = await getProjectTasks(parseInt(projectId))
        setTasks(updatedTasks)
      }
      
      notify.success('Task status updated')
    } catch (err) {
      notify.error('Failed to update task status')
      console.error(err)
    }
  }
  
  const handleDeleteTask = async (taskId: number) => {
    try {
      await deleteTask(taskId)
      
      // Refresh tasks
      if (projectId) {
        const updatedTasks = await getProjectTasks(parseInt(projectId))
        setTasks(updatedTasks)
      }
      
      notify.success('Task deleted')
    } catch (err) {
      notify.error('Failed to delete task')
      console.error(err)
    }
  }

  const projectSquadIsEmpty = (categorizedPersonas.project_personas.length === 0)
  
  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      
      <div className="container mx-auto px-0 sm:px-4 pb-8 sm:pt-8">
        
        <div className="bg-white p-4 rounded-lg shadow-md mb-8">
          {editMode ? (
            <form onSubmit={handleSaveEdit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title*
                  </label>
                  <input
                    type="text"
                    value={editedProject.title}
                    onChange={(e) => setEditedProject({...editedProject, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                    required
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editedProject.description || ''}
                    onChange={(e) => setEditedProject({...editedProject, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                    rows={3}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emoji
                  </label>
                  <input
                    type="text"
                    value={editedProject.emoji || ''}
                    onChange={(e) => setEditedProject({...editedProject, emoji: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                    placeholder="📚"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cover Picture URL
                  </label>
                  <input
                    type="text"
                    value={editedProject.cover_pic_s3_key || ''}
                    onChange={(e) => setEditedProject({...editedProject, cover_pic_s3_key: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                    placeholder="https://..."
                  />
                </div>
                
{/* Category Management in Edit Mode */}
                <div className="col-span-2 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Categories</h3>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {project?.categories?.filter(c => c.manual_override !== ManualOverrideType.DELETE).map((category) => {
                      const isManuallyOverridden = category.manual_override === ManualOverrideType.KEEP_VALUE;
                      return (
                        <div
                          key={category.id}
                          className="inline-flex items-center rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-200"
                        >
                          <span className="pl-2.5 py-0.5" title={category.description || category.name}>
                            {category.icon && <span className="mr-1">{category.icon}</span>}
                            {category.name}
                          </span>
                          {isManuallyOverridden && (
                            <>
                              <div className="h-4 mx-1 border-l border-gray-300"></div>
                              <div className="text-gray-600 pr-1" title="Manually set category">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </>
                          )}
                          <div className="h-4 mx-1 border-l border-gray-300"></div>
                          <button 
                            type="button"
                            onClick={() => handleCategoryToggle(category)}
                            className="pr-2 py-0.5 text-gray-600 hover:text-gray-800"
                            title="Remove category"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setShowCategorySelector(true)}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600 hover:bg-gray-300 border-2 border-dashed border-gray-400"
                    >
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Add Category
                    </button>
                  </div>
                </div>
                
                <div className="col-span-2 flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className={`bg-action text-plum px-4 py-2 rounded-md hover:bg-action-dark flex items-center ${
                      saving ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                {/* Left side - Project info */}
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="text-2xl font-bold text-gray-800">
                      {project.emoji && <span className="mr-1">{project.emoji}</span>}
                      {project.title}
                    </h1>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      project.status === ProjectStatus.DRAFT
                        ? 'bg-yellow-100 text-yellow-800'
                        : project.status === ProjectStatus.ACTIVE
                        ? 'bg-green-100 text-green-800'
                        : project.status === ProjectStatus.COMPLETED
                        ? 'bg-amber-100 text-amber-900'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  
                  {project.description && (
                    <div className="mb-2">
                      <p
                        ref={descriptionRef}
                        className={`text-gray-600 text-sm whitespace-pre-wrap ${descExpanded ? '' : 'line-clamp-2'}`}
                      >
                        {project.description}
                      </p>
                      {(descOverflows || descExpanded) && (
                        <motion.button
                          type="button"
                          onClick={() => setDescExpanded((v) => !v)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                          className="mt-1 text-xs text-amber-800 hover:text-amber-900 cursor-pointer"
                        >
                          {descExpanded ? 'Show less' : 'Show more'}
                        </motion.button>
                      )}
                    </div>
                  )}
                  
                  {/* Project Categories */}
                  {project.categories && project.categories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {project.categories.filter(c => c.manual_override !== ManualOverrideType.DELETE).map((category) => {
                        const isManuallyOverridden = category.manual_override === ManualOverrideType.KEEP_VALUE;
                        return (
                          <div
                            key={category.id}
                            className="inline-flex items-center rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-200"
                          >
                            <span className={`pl-2.5 py-0.5 ${isManuallyOverridden ? '' : 'pr-2.5'}`} title={category.description || category.name}>
                              {category.icon && <span className="mr-1">{category.icon}</span>}
                              {category.name}
                            </span>
                            {isManuallyOverridden && (
                              <>
                                <div className="h-4 mx-1 border-l border-gray-300"></div>
                                <div className="text-gray-600 pr-1" title="Manually set category">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    {project.start_date && (
                      <div className="flex items-center gap-1">
                        <span>Start:</span>
                        <span className="font-medium">{formatDate(project.start_date)}</span>
                      </div>
                    )}
                    
                    {project.end_date && (
                      <div className="flex items-center gap-1">
                        <span>End:</span>
                        <span className="font-medium">{formatDate(project.end_date)}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1">
                      <span>Created</span>
                      <span className="font-medium">{formatMonthYear(project.created_at)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Right side - Action buttons */}
                <div className="flex flex-wrap gap-2 items-start">
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-3 py-1 bg-action text-plum text-sm rounded hover:bg-action-dark"
                  >
                    Edit
                  </button>
                  
                  <div className="relative status-dropdown-container">
                    <button
                      onClick={() => setStatusMenuOpen(!statusMenuOpen)}
                      className="px-3 py-1 bg-secondary text-gray-700 text-sm rounded hover:bg-secondary-dark"
                    >
                      Actions
                    </button>
                    <div
                      className={`absolute left-0 sm:left-auto sm:right-0 z-10 mt-1 w-48 origin-top-left sm:origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none ${statusMenuOpen ? '' : 'hidden'}`}
                    >
                      {project.status !== ProjectStatus.ACTIVE && (
                        <button
                          onClick={() => {
                            handleStatusChange(ProjectStatus.ACTIVE);
                            setStatusMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Activate
                        </button>
                      )}

                      {project.status !== ProjectStatus.ARCHIVED && (
                        <button
                          onClick={() => {
                            handleStatusChange(ProjectStatus.ARCHIVED);
                            setStatusMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Archive
                        </button>
                      )}

                      <div className="my-1 border-t border-gray-200"></div>
                      <button
                        onClick={() => {
                          setStatusMenuOpen(false);
                          setShowDeleteConfirm(true);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50"
                      >
                        Delete…
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* View Checkins Button Row */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Link
                  to={`/checkins?project=${project.id}`}
                  className="inline-flex items-center px-4 py-2 bg-action text-plum text-sm rounded-md hover:bg-action-dark transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  View Project Checkins
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Triggered advice notification (advising overhaul Stage 6) — slim
            card surfaced above the Activity Calendar when an advice trigger
            has fired for this project. Self-hides when there's nothing
            pending. */}
        <TriggeredAdviceCard projectId={parseInt(projectId!)} />

        {/* Activity Calendar — hidden entirely when the project has zero checkins ever. */}
        {activityEmpty !== true && (
          <div className="bg-white p-6 sm:rounded-lg sm:shadow-md mb-8">
            <ProjectActivityCalendar
              projectId={parseInt(projectId!)}
              onLifetimeEmptyChange={setActivityEmpty}
            />
          </div>
        )}

        {/* Places visited on the project's checkins — hidden when there are none ever. */}
        {placesEmpty !== true && (
          <div className="bg-white p-6 sm:rounded-lg sm:shadow-md mb-8">
            <ProjectPlacesSection
              projectId={parseInt(projectId!)}
              onLifetimeEmptyChange={setPlacesEmpty}
            />
          </div>
        )}

        {/* People tagged on the project's checkins — hidden when there are none ever. */}
        {peopleEmpty !== true && (
          <div className="bg-white p-6 sm:rounded-lg sm:shadow-md mb-8">
            <ProjectPeopleSection
              projectId={parseInt(projectId!)}
              onLifetimeEmptyChange={setPeopleEmpty}
            />
          </div>
        )}

        {/* Project Insights — Stage 8: `getProjectSummary` now reads the
            themes/lessons/squad_thoughts from the new activity-summary system
            (the project_narrative builder folds them into
            summary_results.numeric_payload), so `projectSummary` is sourced
            from the new pipeline. ProjectInsightsCard renders those plus the
            narrative (text_summary, fetched read-only). */}
        {loadingStates.summary ? (
          <div className="bg-white p-6 sm:rounded-lg sm:shadow-md mb-8">
            <div className="flex justify-center items-center h-32">
              <Spinner />
            </div>
          </div>
        ) : (
          <ProjectInsightsCard
            projectId={parseInt(projectId!)}
            legacy={projectSummary}
          />
        )}

        {/* Project summaries (Stage 17 load model): every summary the project
            is involved in, plus <=1 per member feature. project_narrative is
            excluded — the Insights card above already renders it. */}
        {project && (() => {
          const pid = parseInt(projectId!)
          const cards = summariesForProject(summaryConfigs, pid, associatedFeatureIds)
            .filter((c) => c.kind !== 'project_narrative')
          return (
            <div className="bg-white p-6 sm:rounded-lg sm:shadow-md mb-8">
              <AddSummaryButton
                surface="project"
                projectId={pid}
                onChanged={reloadSummaryConfigs}
              />
              {cards.length === 0 ? (
                <div className="py-2 text-sm text-gray-400">No summaries here yet.</div>
              ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {cards.map((config) => {
                    const fid = config.source_feature_ids?.[0]
                    const feat = allFeatures.find((f) => f.id === fid)
                    return (
                      <FeatureSummaryCard
                        key={config.id}
                        config={config}
                        featureName={feat?.title}
                        unit={feat?.unit}
                        surface="project"
                        projectId={pid}
                        onChanged={reloadSummaryConfigs}
                      />
                    )
                  })}
                </div>
              )}
            </div>
          )
        })()}

        {/* Section 1: Features */}
        <Suspense fallback={
          <div className="bg-white p-6 sm:rounded-lg sm:shadow-md mb-8">
            <div className="flex justify-center items-center h-32">
              <Spinner />
            </div>
          </div>
        }>
          <ProjectFeatures
            projectId={parseInt(projectId!)}
            associatedFeatureIds={associatedFeatureIds}
            allFeatures={allFeatures}
            onFeaturesUpdate={handleFeaturesUpdate}
            onSuccess={notify.success}
            onError={notify.error}
          />
        </Suspense>
        
        {/* Section 2: Squad. The old "Advice" sibling tab here was the
            legacy coaching chat list; Stage 7 of the advising overhaul
            deleted it. Triggered advice now surfaces above the Activity
            Calendar via TriggeredAdviceCard, and the advice index lives
            at /advice. */}
        <div className="bg-white p-6 sm:rounded-lg sm:shadow-md mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Squad</h2>

          {/* Advisor suggestions (Stage 6) — system-Genie-loop proposed
              advisors per project. Self-hides when empty. Sits above
              the existing project-squad section header. */}
              <AdvisorSuggestionsList
                projectId={parseInt(projectId!)}
                onSquadChanged={async () => {
                  if (!projectId) return
                  const personas = await getProjectPersonasCategories(parseInt(projectId))
                  setCategorizedPersonas(personas)
                  setProjectPersonaIds(personas.project_personas.map(p => p.id))
                }}
              />

              {/* Project Squad Section */}
              <div className="mb-6">
                {projectSquadIsEmpty ? (
                  <>
                    {/* Stage 6 empty-state nudge — shows ONE suggestion with
                        friendlier copy. Dismissible (persists). Sits ABOVE
                        the existing empty-state copy. */}
                    <EmptyProjectNudge
                      projectId={parseInt(projectId!)}
                      onSquadChanged={async () => {
                        if (!projectId) return
                        const personas = await getProjectPersonasCategories(parseInt(projectId))
                        setCategorizedPersonas(personas)
                        setProjectPersonaIds(personas.project_personas.map(p => p.id))
                      }}
                    />
                    <div className="w-full text-center py-6 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">
                        Add advisors whose opinions you value about <span className="font-medium">{project.title}{" "}</span>
                        or{" "}
                        <button
                          onClick={() => setShowPersonaCreator(true)}
                          className="text-amber-800 hover:text-amber-900 hover:underline font-medium"
                        >
                          create your own advisor from scratch
                        </button>.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                    {categorizedPersonas.project_personas.map(persona => (
                      <PersonaCard
                        key={persona.id}
                        persona={persona}
                        onPersonaUpdated={handlePersonaUpdated}
                        projectCategoryIds={project.categories?.map(c => c.id) || []}
                        editActionButtons={
                          <button
                            onClick={() => handleRemovePersona(persona.id)}
                            className="px-3 py-2 bg-rose-100 text-rose-700 rounded-md hover:bg-rose-200"
                          >
                            Remove from Project
                          </button>
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* Search Section */}
              <div className="mb-6 pt-4">
                <h3 className="text-lg font-medium text-gray-700 mb-4">Add Advisors To Project</h3>
                <PersonaSearch 
                  onSearchResults={setSearchResults}
                  className="max-w-md"
                />
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-700 mb-4">Search Results ({searchResults.length})</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {searchResults.map(persona => {
                      const isPersonaInProject = isInProject(persona.id)
                      return (
                        <PersonaCard
                          key={persona.id}
                          persona={persona}
                          onPersonaUpdated={handlePersonaUpdated}
                          isAdmin={user?.isAdmin}
                          isInSquad={isPersonaInProject}
                          projectCategoryIds={project.categories?.map(c => c.id) || []}
                          actionButtons={
                            !isPersonaInProject && (
                              <button
                                onClick={() => handleAddPersona(persona.id)}
                                className="px-3 py-2 bg-action text-plum rounded-md hover:bg-action-dark"
                              >
                                Add to Project
                              </button>
                            )
                          }
                        />
                      )
                    })}
                  </div>
                </div>
              )}
              
              {/* Bench Section - hide when searching */}
              <div className={`mb-6 pt-4 ${searchResults.length > 0 ? 'hidden' : ''}`}>
                <h3 className="text-lg font-medium text-gray-700 mb-4">
                    Account Squad
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      (Advise outside this project)
                    </span>
                </h3>
                {categorizedPersonas.bench_personas.length === 0 ? (
                  <div className="w-full text-center py-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">No advisors. All your advisors are either on this project or not yet in your squad.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                    {categorizedPersonas.bench_personas.map(persona => (
                      <PersonaCard
                        key={persona.id}
                        persona={persona}
                        onPersonaUpdated={handlePersonaUpdated}
                        projectCategoryIds={project.categories?.map(c => c.id) || []}
                        isInProject={persona.isInProject}
                        actionButtons={
                          !persona.isInProject && (
                            <button
                              onClick={() => handleAddPersona(persona.id)}
                              className="px-3 py-2 bg-action text-plum rounded-md hover:bg-action-dark"
                            >
                              Add to Project
                            </button>
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* All Advisors Section - hide when searching */}
              <div className={`pt-4 ${searchResults.length > 0 ? 'hidden' : ''}`}>
                <h3 className="text-lg font-medium text-gray-700 mb-4">
                  Top Relevant Advisors
                  {project.categories && project.categories.length > 0 && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      (With relevant expertise)
                    </span>
                  )}
                </h3>
                
                <PersonaCreator 
                  isOpen={showPersonaCreator}
                  onClose={() => setShowPersonaCreator(false)}
                  isAdmin={user?.isAdmin}
                  onPersonaCreated={(persona) => {
                    // Add new persona to the bench personas since it's automatically added to the user's squad
                    setCategorizedPersonas({
                      ...categorizedPersonas,
                      bench_personas: [...categorizedPersonas.bench_personas, persona]
                    });
                    notify.success(`Created new persona: ${persona.first_name}`);
                  }}
                />
                
                {categorizedPersonas.all_personas.length === 0 ? (
                  <div className="w-full text-center py-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">
                      No additional advisors available. Use the lists above or{" "}
                      <button
                        onClick={() => setShowPersonaCreator(true)}
                        className="text-amber-800 hover:text-amber-900 hover:underline font-medium"
                      >
                        create your own advisor from scratch
                      </button>.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <CreateAdvisorCard onClick={() => setShowPersonaCreator(true)} />
                    
                    {/* Existing Personas */}
                    {categorizedPersonas.all_personas.map(persona => (
                      <PersonaCard
                        key={persona.id}
                        persona={persona}
                        onPersonaUpdated={handlePersonaUpdated}
                        projectCategoryIds={project.categories?.map(c => c.id) || []}
                        actionButtons={
                          <button
                            onClick={() => handleAddPersona(persona.id)}
                            className="px-3 py-2 bg-action text-plum rounded-md hover:bg-action-dark"
                          >
                            Add to Project
                          </button>
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
        </div>

        {/* Section 3: Tasks (no tabs) */}
                  {/*<h3 className="text-lg font-medium text-gray-700">Project Tasks</h3>*/}
        <div className="bg-white p-6 sm:rounded-lg sm:shadow-md mb-8">
              {/* Tasks Section */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 md:mb-0">Tasks</h2>
                  <button
                    onClick={() => setShowAddTask(!showAddTask)}
                    disabled={savingTask}
                    className={`bg-action text-plum px-4 py-2 rounded-md hover:bg-action-dark ${
                      savingTask ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {showAddTask ? 'Cancel' : 'Add Task'}
                  </button>
                </div>

                {/* Filter Controls */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => setTaskStatusFilter('all')}
                    className={`px-3 py-1 rounded-md text-sm ${
                      taskStatusFilter === 'all'
                        ? 'bg-action text-plum'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setTaskStatusFilter(TaskStatus.PENDING)}
                    className={`px-3 py-1 rounded-md text-sm ${
                      taskStatusFilter === TaskStatus.PENDING
                        ? 'bg-action text-plum'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => setTaskStatusFilter(TaskStatus.COMPLETED)}
                    className={`px-3 py-1 rounded-md text-sm ${
                      taskStatusFilter === TaskStatus.COMPLETED
                        ? 'bg-action text-plum'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Completed
                  </button>
                  <button
                    onClick={() => setTaskStatusFilter(TaskStatus.ARCHIVED)}
                    className={`px-3 py-1 rounded-md text-sm ${
                      taskStatusFilter === TaskStatus.ARCHIVED
                        ? 'bg-action text-plum'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Archived
                  </button>
                </div>

                {/* Tasks Content with Loading Overlay */}
                <div className={`relative ${savingTask ? 'opacity-50 pointer-events-none' : ''}`}>
                  {savingTask && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="bg-white rounded-lg shadow-lg p-4 flex items-center">
                        <svg className="animate-spin h-5 w-5 mr-3 text-amber-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-gray-700">Creating task...</span>
                      </div>
                    </div>
                  )}

                  {/* Add Task Form */}
                  {showAddTask && (
                    <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
                      <h4 className="text-md font-medium mb-3">Add New Task</h4>
                      <form onSubmit={handleCreateTask}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Task Description*
                            </label>
                            <textarea
                              value={newTaskText}
                              onChange={(e) => setNewTaskText(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                              rows={2}
                              placeholder="Describe what needs to be done..."
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Due Date (Optional)
                            </label>
                            <input
                              type="date"
                              value={newTaskDueDate}
                              onChange={(e) => setNewTaskDueDate(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                            />
                            {newTaskDueDate && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Due Time (Optional)
                                </label>
                                <input
                                  type="time"
                                  value={newTaskDueTime}
                                  onChange={(e) => setNewTaskDueTime(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddTask(false)
                              setNewTaskText('')
                              setNewTaskDueDate('')
                              setNewTaskDueTime('')
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={savingTask || !newTaskText.trim()}
                            className={`bg-action text-plum px-4 py-2 rounded-md hover:bg-action-dark flex items-center ${
                              (savingTask || !newTaskText.trim()) ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            {savingTask ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Creating...
                              </>
                            ) : 'Create Task'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Tasks List */}
                  {(() => {
                    const filteredTasks = taskStatusFilter === 'all' 
                      ? tasks 
                      : tasks.filter(task => task.status === taskStatusFilter)
                    
                    if (filteredTasks.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          <p>
                            {taskStatusFilter === 'all' 
                              ? "No tasks for this project yet. Add tasks to track what needs to be done."
                              : `No ${taskStatusFilter} tasks found.`
                            }
                          </p>
                        </div>
                      )
                    }

                  return (
                    <div className="space-y-3" ref={taskListParent}>
                      {filteredTasks.map(task => (
                        <div key={task.id} className="border border-gray-200 rounded-md p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-grow">
                              <p className="text-gray-800 mb-2">{task.text_body}</p>
                              
                              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                {task.due_date && (
                                  <div className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                    </svg>
                                    <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                                  </div>
                                )}
                                {task.due_at && (
                                  <div className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                    <span>At: {new Date(task.due_at).toLocaleString()}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1">
                                  <span>Created: {new Date(task.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 ml-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                task.status === TaskStatus.PENDING
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : task.status === TaskStatus.COMPLETED
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {task.status}
                              </span>
                              
                              {/* Status change buttons */}
                              {task.status === TaskStatus.PENDING && (
                                <button
                                  onClick={() => handleTaskStatusChange(task.id, TaskStatus.COMPLETED)}
                                  className="text-green-600 hover:text-green-800 cursor-pointer"
                                  title="Mark as completed"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              )}
                              
                              {task.status === TaskStatus.COMPLETED && (
                                <button
                                  onClick={() => handleTaskStatusChange(task.id, TaskStatus.PENDING)}
                                  className="text-yellow-600 hover:text-yellow-800 cursor-pointer"
                                  title="Mark as pending"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              )}
                              
                              <button
                                onClick={() => handleDeleteTask(task.id)}
                                className="text-rose-500 hover:text-rose-700 cursor-pointer"
                                title="Delete task"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                  })()}
                </div>
              </div>
        </div>
      </div>
      
      {/* Category Selector Modal */}
      {showCategorySelector && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Category</h3>
            <div className="space-y-2 mb-6">
              {allCategories.map((category) => {
                const isCurrentlyAssigned = project?.categories?.some(c => c.id === category.id && c.manual_override !== ManualOverrideType.DELETE);
                return (
                  <div key={category.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`category-${category.id}`}
                      checked={isCurrentlyAssigned}
                      onChange={() => handleCategoryToggle(category)}
                      className="h-4 w-4 text-amber-800 border-gray-300 rounded focus:ring-action"
                    />
                    <label htmlFor={`category-${category.id}`} className="ml-3 flex items-center cursor-pointer">
                      {category.icon && <span className="mr-2">{category.icon}</span>}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{category.name}</div>
                        {category.description && (
                          <div className="text-xs text-gray-500">{category.description}</div>
                        )}
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCategorySelector(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Project</h3>
            {/* Specific about what goes and what stays, because the two
                halves are not obvious and the action is irreversible. The
                advice lines earned their place on 2026-09-25, when
                `triggered_advice` and `project_advisor_suggestions` were
                given cascading FKs on project_id — before that they were
                orphaned rather than deleted, and the dialog said nothing
                either way. The chats are the exception worth naming:
                `genie_chats.project_id` has no FK, so an advice conversation
                outlives the project it was about and stays under Advice. */}
            <div className="text-gray-600 mb-6 space-y-3">
              <p>
                Are you sure you want to delete "{project.title}"? This cannot be undone.
              </p>
              <p>
                Its features, squad and chats go with it, along with its advisor
                suggestions and any advice it triggered. The advice conversations
                themselves stay under Advice.
              </p>
              <p>
                Your check-ins and tasks aren't deleted — they lose their link to this
                project and nothing else.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProject}
                className="px-4 py-2 bg-danger text-white rounded-md hover:bg-danger-dark"
              >
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectDetailPage